import {
  WSTable,
  WSTableColumnSortDirection,
  WSTableHeaderAction,
  WSTableItem
} from "@wingspanhq/fe-component-library";
import { PayableRow } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useCustomization } from "../../../customization";
import { ColumnKey, getColumns } from "./columns";
import { SortField } from "./filters/types";

export type Props = {
  payables: PayableRow[];
  loading?: boolean;
  onRowClick?: (payable: PayableRow) => void;
  onSortChange?: (
    field: SortField,
    direction: WSTableColumnSortDirection
  ) => void;
  sortField?: SortField;
  sortDirection?: WSTableColumnSortDirection;
  selection?: WSTableItem<PayableRow>[];
  onSelectionChange?: (selection: WSTableItem<PayableRow>[]) => void;
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  // CSV download is rendered inside the table chrome (top-right header action),
  // mirroring legacy PayablesTable. Only shown when `canDownloadCsv` is true.
  canDownloadCsv?: boolean;
  isDownloadingCsv?: boolean;
  onDownloadCsv?: () => void;
};

export const TablePayables: React.FC<Props> = props => {
  const { terminology } = useCustomization();

  const tableData = useMemo<WSTableItem<PayableRow>[]>(
    () =>
      props.payables.map(payable => ({
        id: payable.id ?? "",
        data: payable
      })),
    [props.payables]
  );

  const columns = useMemo(
    () =>
      getColumns({
        visibleColumns: props.visibleColumns,
        sortField: props.sortField,
        sortDirection: props.sortDirection,
        paidAtLabel: terminology().sendPaymentsPaidAt,
        onSortChange: props.onSortChange
      }),
    [
      props.visibleColumns,
      props.sortField,
      props.sortDirection,
      props.onSortChange,
      terminology
    ]
  );

  const handleRowClick = props.onRowClick
    ? (item: WSTableItem<PayableRow>) => props.onRowClick!(item.data)
    : undefined;

  const headerAction = useMemo<WSTableHeaderAction | undefined>(() => {
    if (!props.canDownloadCsv || !props.onDownloadCsv) {
      return undefined;
    }
    return {
      icon: "download",
      onClick: props.onDownloadCsv,
      loading: props.isDownloadingCsv
    };
  }, [props.canDownloadCsv, props.onDownloadCsv, props.isDownloadingCsv]);

  return (
    <WSTable<PayableRow>
      loading={props.loading}
      columns={columns}
      tableData={tableData}
      onRowClick={handleRowClick}
      selection={props.selection}
      onSelectionChange={props.onSelectionChange}
      headerAction={headerAction}
    />
  );
};
